import { useState } from "react"
import { CopyableAddress } from "../ui/CopyableAddress"
import { APP_VERSION, validateActiveRpcDomain, selectableNetworksFor } from "../../lib/config"
import { NotificationBell } from "./NotificationBell"
import type { Notification } from "../../lib/notifications"
import { PRO_APP_ENABLED } from "../../lib/professionalFlags"
import { ThemeToggle } from "../ui/ThemeSelect"

// ── Types ──────────────────────────────────────────────────────────────
interface TopBarProps {
    adena: {
        connected: boolean
        address: string
        chainId: string
        rpcTrusted: boolean
        rpcUrl: string
        loading: boolean
        reconnecting: boolean
        installed: boolean
        connect: () => void
        pubkeyJSON: string
    }
    auth: {
        isAuthenticated: boolean
        address: string | null
    }
    compactBalance: string
    network: {
        networkKey: string
        chainId: string
        networks: Record<string, { label: string; chainId: string; rpcUrl: string; hidden?: boolean }>
        switchNetwork: (key: string) => void
    }
    isLoggingIn: boolean
    authError: string | null
    onDisconnect: () => void
    onClearError: () => void
    onRetry: () => void
    notifications: {
        notifications: Notification[]
        unreadCount: number
        markRead: (id: string) => void
        markAllRead: () => void
    }
    /** Programmatically add + switch Adena wallet to a network. */
    addAndSwitchWalletNetwork?: (chainId: string, chainName: string, rpcUrl: string) => Promise<boolean>
    /** Callback when wallet network switch succeeds. */
    onWalletSwitchSuccess?: (chainName: string) => void
    /** B3: Toggle sidebar on tablet viewports. */
    onToggleSidebar?: () => void
}

// ── TopBar Component ───────────────────────────────────────────────────
export function TopBar({ adena, auth, compactBalance, network, isLoggingIn, authError, onDisconnect, onClearError, onRetry, notifications, addAndSwitchWalletNetwork, onWalletSwitchSuccess, onToggleSidebar }: TopBarProps) {
    return (
        <>
            <header className="k-topbar" role="banner" data-testid="topbar">
                {/* B3: Hamburger toggle — visible only on tablet (768-1024px) via CSS */}
                {onToggleSidebar && (
                    <button
                        className="k-topbar-hamburger"
                        onClick={onToggleSidebar}
                        aria-label="Toggle sidebar"
                        title="Toggle sidebar"
                    >
                        ☰
                    </button>
                )}
                {/* Left: badges */}
                <div className="k-topbar-left">
                    <span className="k-topbar-badge k-topbar-badge--alpha" data-testid="alpha-badge">Alpha</span>
                    <span className="k-topbar-badge k-topbar-badge--version" data-testid="version-badge">{PRO_APP_ENABLED ? `v${APP_VERSION}` : "v3"}</span>
                </div>

                {/* Right: network + wallet */}
                <div className="k-topbar-right">
                    {/* Theme toggle */}
                    <ThemeToggle />

                    {/* Network selector */}
                    <select
                        className="k-topbar-network-select"
                        value={network.networkKey}
                        onChange={(e) => network.switchNetwork(e.target.value)}
                        title="Switch network"
                    >
                        {Object.entries(selectableNetworksFor(network.networkKey)).map(([key, net]) => (
                            <option key={key} value={key}>
                                {net.label}
                            </option>
                        ))}
                    </select>

                    {/* Notification bell (only when connected) */}
                    {adena.connected && auth.isAuthenticated && (
                        <NotificationBell
                            notifications={notifications.notifications}
                            unreadCount={notifications.unreadCount}
                            onMarkRead={notifications.markRead}
                            onMarkAllRead={notifications.markAllRead}
                        />
                    )}

                    {/* Wallet area */}
                    {adena.connected && auth.isAuthenticated ? (
                        <>
                            <span className="k-topbar-balance">
                                {compactBalance}
                            </span>
                            <div className="k-topbar-wallet-row">
                                <span className="k-status-dot k-status-dot--ok animate-glow" />
                                <span className="k-topbar-wallet-addr">
                                    <CopyableAddress address={auth.address || adena.address} compact={true} fontSize={12} />
                                </span>
                            </div>
                            <button className="k-topbar-disconnect" onClick={onDisconnect}>
                                Disconnect
                            </button>
                        </>
                    ) : isLoggingIn ? (
                        <span className="k-btn-wallet" style={{ cursor: "default", opacity: 0.7 }}>
                            <span className={`k-status-dot ${adena.reconnecting ? "k-status-dot--ok" : "k-status-dot--warn"} animate-glow`} />
                            {adena.reconnecting ? "Syncing..." : "Authenticating..."}
                        </span>
                    ) : adena.reconnecting ? (
                        /* B3: Sync timed out — show retry */
                        <span className="k-btn-wallet" style={{ cursor: "pointer", opacity: 0.8, borderColor: "rgba(245,166,35,0.3)" }} onClick={() => window.location.reload()}>
                            <span className="k-status-dot k-status-dot--warn" />
                            Sync timeout — Retry
                        </span>
                    ) : (
                        adena.installed ? (
                            <button className="k-btn-wallet" onClick={adena.connect} disabled={isLoggingIn}>
                                <span className="k-status-dot k-status-dot--ok" />
                                Connect Wallet
                            </button>
                        ) : (
                            <a
                                href="https://adena.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="k-btn-wallet"
                                style={{ textDecoration: "none" }}
                            >
                                <span className="k-status-dot k-status-dot--warn" />
                                Install Adena
                            </a>
                        )
                    )}
                </div>
            </header>

            {/* ── Auth error banner ──────────────────────────────── */}
            {authError && (
                <div className="k-topbar-banner k-topbar-banner--error" role="alert">
                    <span className="k-topbar-banner__text k-topbar-banner__text--error">
                        ⚠ {authError}
                    </span>
                    <button
                        onClick={onRetry}
                        style={{
                            background: "transparent", border: "1px solid currentColor",
                            color: "inherit", borderRadius: 4, padding: "2px 10px",
                            fontSize: "var(--pro-small, 12px)", fontWeight: 600, cursor: "pointer", marginRight: 4,
                        }}
                    >
                        Retry
                    </button>
                    <button className="k-topbar-banner__dismiss" onClick={onClearError}>
                        ×
                    </button>
                </div>
            )}

            {/* ── Chain mismatch warning ─────────────────────────── */}
            {adena.connected && adena.chainId && network.chainId !== adena.chainId && (
                <ChainMismatchBanner
                    walletChainId={adena.chainId}
                    membaChainId={network.chainId}
                    networks={network.networks}
                    switchMembaNetwork={network.switchNetwork}
                    addAndSwitchWallet={addAndSwitchWalletNetwork}
                    onSwitchSuccess={onWalletSwitchSuccess}
                />
            )}

            {/* ── Untrusted wallet RPC warning ──────────────────── */}
            {adena.connected && !adena.rpcTrusted && (
                <div className="k-security-banner" role="alert">
                    <div className="k-security-banner__header">
                        <span style={{ fontSize: 18 }}>🛡️</span>
                        <span className="k-security-banner__title">
                            SECURITY WARNING
                        </span>
                        <span className="k-security-banner__subtitle">
                            — All transactions are blocked
                        </span>
                    </div>
                    <div className="k-security-banner__body">
                        {adena.rpcUrl ? (
                            <>
                                Your wallet is connected to an untrusted RPC:{" "}
                                <code className="k-security-banner__code">
                                    {adena.rpcUrl}
                                </code>
                            </>
                        ) : (
                            <>Unable to verify your wallet&apos;s RPC URL.</>
                        )}
                        <br />
                        <span className="k-security-banner__action">
                            Open Adena → Settings → Networks → switch to a *.gno.land RPC
                        </span>
                    </div>
                </div>
            )}

            {/* ── Defense-in-depth: config RPC check ────────────── */}
            {(() => {
                const rpcWarning = validateActiveRpcDomain()
                if (!rpcWarning || (adena.connected && !adena.rpcTrusted)) return null
                return (
                    <div className="k-topbar-banner k-topbar-banner--security">
                        <span style={{ fontSize: 16 }}>🛡️</span>
                        <span className="k-topbar-banner__text k-topbar-banner__text--error" style={{ fontWeight: 600 }}>
                            SECURITY WARNING: {rpcWarning}
                        </span>
                    </div>
                )
            })()}
        </>
    )
}

// ── Chain Mismatch Banner ─────────────────────────────────────────────

export function ChainMismatchBanner({
    walletChainId,
    membaChainId,
    networks,
    switchMembaNetwork,
    addAndSwitchWallet,
    onSwitchSuccess,
}: {
    walletChainId: string
    membaChainId: string
    networks: Record<string, { label: string; chainId: string; rpcUrl: string; hidden?: boolean }>
    switchMembaNetwork: (key: string) => void
    addAndSwitchWallet?: (chainId: string, chainName: string, rpcUrl: string) => Promise<boolean>
    onSwitchSuccess?: (chainName: string) => void
}) {
    const [switching, setSwitching] = useState(false)

    // NETWORKS is keyed by an identifier-safe KEY (e.g. "test13") while the
    // on-wire chainId is hyphenated ("test-13"). Resolve the wallet's chainId to
    // its KEY before treating it as a known network / calling switchMembaNetwork
    // (which takes a KEY). Looking it up as a key breaks only for test13.
    const walletKey = Object.keys(networks).find(k => networks[k].chainId === walletChainId)
    // Resolve against the FULL map so the banner still names the wallet's chain
    // correctly — but only OFFER to follow it there when we still offer that
    // network. This was the fourth surface that could put a user on Betanet, and
    // the worst one: it renders only while a wallet is connected, i.e. exactly the
    // cohort that hits F-29 (login succeeds on gnoland1, then every call 401s).
    // For a hidden network the honest advice is the other branch — move the WALLET
    // to the chain Memba is on.
    const walletInMemba = !!walletKey && !networks[walletKey].hidden
    const membaNet = networks[Object.keys(networks).find(k => networks[k].chainId === membaChainId) || ""]

    const handleAddAndSwitch = async () => {
        if (!addAndSwitchWallet || !membaNet || switching) return
        setSwitching(true)
        try {
            const ok = await addAndSwitchWallet(membaNet.chainId, membaNet.label, membaNet.rpcUrl)
            if (ok) onSwitchSuccess?.(membaNet.label)
        } finally {
            setSwitching(false)
        }
    }

    return (
        <div className="k-topbar-banner k-topbar-banner--warning">
            <span className="k-topbar-banner__text k-topbar-banner__text--warning">
                ⚠ Network mismatch — wallet is on <strong>{walletChainId}</strong>, Memba is on <strong>{membaChainId}</strong>
            </span>
            {walletInMemba && walletKey ? (
                <button
                    className="k-topbar-banner__btn k-topbar-banner__btn--warning"
                    onClick={() => switchMembaNetwork(walletKey)}
                >
                    Switch Memba to {networks[walletKey].label}
                </button>
            ) : addAndSwitchWallet && membaNet ? (
                <button
                    className={`k-topbar-banner__btn ${switching ? "k-topbar-banner__btn--switching" : "k-topbar-banner__btn--accent"}`}
                    onClick={handleAddAndSwitch}
                    disabled={switching}
                    aria-busy={switching}
                >
                    {switching ? "Switching…" : `Add & Switch Wallet to ${membaChainId}`}
                </button>
            ) : (
                <span className="k-topbar-banner__text k-topbar-banner__text--muted">
                    Switch your wallet to {membaChainId} in Adena
                </span>
            )}
        </div>
    )
}
